import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApiQuery } from '../use-api.js';
import { isTerminal } from '../lib/status.js';
import type { LogsResponse } from '../lib/types.js';

export function RunLogs() {
  const { id = '' } = useParams();

  // Accumulate log lines across pages. `nextToken` drives pagination; we hold
  // it in state and feed it back as the `next` query param.
  const [lines, setLines] = useState<string[]>([]);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('');

  const params = new URLSearchParams();
  if (nextToken) params.set('next', nextToken);
  const qs = params.toString();
  const path = `/api/runs/${encodeURIComponent(id)}/logs${qs ? `?${qs}` : ''}`;

  const query = useApiQuery<LogsResponse>(
    ['logs', id, nextToken ?? ''],
    path,
    {
      // Poll while the run is non-terminal; stop once it reaches a terminal
      // status. We read the latest known status from component state.
      refetchInterval: status && isTerminal(status) ? false : 2000,
    },
  );

  useEffect(() => {
    const data = query.data;
    if (!data) return;
    if (data.lines.length > 0) {
      setLines((prev) => [...prev, ...data.lines]);
    }
    setStatus(data.status);
    // Advance the page token only when the server hands us a new one, so the
    // next fetch pulls the following page. When nextToken is null the poll
    // re-requests the same (last) page for live tailing.
    if (data.nextToken && data.nextToken !== nextToken) {
      setNextToken(data.nextToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data]);

  return (
    <section>
      <div className="detail-head">
        <h2>Logs — {id}</h2>
        <Link className="btn-link" to={`/runs/${encodeURIComponent(id)}`}>← Back to run</Link>
      </div>

      {status && (
        <p className="log-status">
          Status: {status}{isTerminal(status) ? ' (terminal — polling stopped)' : ' (live)'}
        </p>
      )}

      {query.isLoading && lines.length === 0 && <p>Loading logs…</p>}
      {query.isError && lines.length === 0 && <p className="error">Failed to load logs.</p>}

      <pre className="log-view">{lines.join('\n')}</pre>
    </section>
  );
}
